import { Col, Flex, Row, Card as AntDCard } from "antd"

import { BodyNormal, Heading3 } from "src/global/ui/typography/Text/Text.tsx"
import { styled, useTheme } from "styled-components"
import { DataQualityIssue } from "src/global/schemas/expectations/expectation-metadata-data-quality-issues.ts"
import {
  DataQualityIssueDefaultExpectationMap,
  selectedExpectationFromSchema,
  useDataQualityFilteredExpectationOptions,
} from "src/global/hooks/useDataQualityFilteredExpectationOptions.ts"
import { SelectedExpectation } from "src/pages/DataAssets/views/Expectations/Expectation/CreateExpectationDrawer/types.ts"
import { useAnalytics } from "src/global/hooks/analytics/useAnalytics.ts"
import { useCallback } from "react"
import { DqiCategoryIcon } from "src/pages/DataAssets/components/DqiCategoryIcon"

const Card = styled(AntDCard)`
  border: 1px solid ${({ theme }) => theme.colors.neutralColorPalette.backgroundsAndBorders.gxBorder};
`

type Props = {
  onSelectExpectation: (selectedExpectation: SelectedExpectation) => void
}

type DataQualityIssueWithDescription = {
  name: DataQualityIssue
  description: string
}

function getDataQualityIssuesAndDescriptions(): DataQualityIssueWithDescription[] {
  return [
    {
      name: "Volume",
      description: "Number of rows",
    },
    {
      name: "Schema",
      description: "Column presence, types, and count",
    },
    {
      name: "Completeness",
      description: "Null and non-null values",
    },
    {
      name: "Uniqueness",
      description: "Unique values and column cardinality",
    },
    {
      name: "Numeric",
      description: "Column value ranges and aggregates",
    },
    {
      name: "Validity",
      description: "Value pattern matching",
    },
    {
      name: "SQL",
      description: "Write your own SQL statement",
    },
    {
      name: "Multi-source",
      description: "Compare SQL query results",
    },
  ]
}

export const DataQualityIssuesCards = ({ onSelectExpectation }: Props) => {
  const { capture } = useAnalytics()
  const theme = useTheme()
  const { valueToSchema } = useDataQualityFilteredExpectationOptions()
  const dataQualityIssues = getDataQualityIssuesAndDescriptions()

  const curryClickHandler = useCallback(
    (dataQualityIssue: DataQualityIssueWithDescription) => {
      return () => {
        onSelectExpectation(
          selectedExpectationFromSchema(
            valueToSchema[DataQualityIssueDefaultExpectationMap[dataQualityIssue.name].schema.title],
            dataQualityIssue.name as DataQualityIssue,
          ),
        )
        capture("expectation_dqi.selected", {
          data_quality_issue: dataQualityIssue.name,
        })
      }
    },
    [capture, onSelectExpectation, valueToSchema],
  )

  return (
    <Flex vertical gap={42}>
      <Row gutter={[8, 8]}>
        {dataQualityIssues.map((dataQualityIssue) => {
          return (
            <Col key={dataQualityIssue.name} span={12}>
              <Card hoverable onClick={curryClickHandler(dataQualityIssue)}>
                <Flex gap={8}>
                  <DqiCategoryIcon
                    dqiCategory={dataQualityIssue.name}
                    color={theme.colors.primaryColors.gxAccentMedium}
                    size="32px"
                    ariaLabel={dataQualityIssue.name}
                  />
                  <Flex vertical gap={8}>
                    <Heading3>{dataQualityIssue.name}</Heading3>
                    <BodyNormal>{dataQualityIssue.description}</BodyNormal>
                  </Flex>
                </Flex>
              </Card>
            </Col>
          )
        })}
      </Row>
    </Flex>
  )
}
